import { InformationCircleIcon, XIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import {
  PatientTableDataFragment,
  ProgramTagDataFragment,
  useAddProgramTagMutation,
  useAutomationRulesQuery,
  useCreateProgramTagMutation,
} from '../../../../../generated/graphql';

import ModalDialog from '../../../../components/ModalDialog';
import ToastAlert from '../../../../components/ToastAlert';
import ProgramTagSelectDropdown from '../../../../components/ProgramTag/ProgramTagSelectDropdown';
import { DEFAULT_TAG_COLOR, getTagStyles } from '../../../../lib/colors';
import { isTagUsedAsAutomationTrigger } from '../../../../lib/automationHelpers';

type BulkAddTagModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedPatients: PatientTableDataFragment[];
  providerProgramTags: ProgramTagDataFragment[];
  refetchMeProviderProgramTags: () => Promise<unknown>;
  onSuccess: () => void | Promise<void>;
};

const BulkAddTagModal: FC<BulkAddTagModalProps> = ({
  isOpen,
  onClose,
  selectedPatients,
  providerProgramTags,
  refetchMeProviderProgramTags,
  onSuccess,
}) => {
  const [selectedTags, setSelectedTags] = useState<ProgramTagDataFragment[]>(
    [],
  );
  const [sessionCreatedTags, setSessionCreatedTags] = useState<
    ProgramTagDataFragment[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [addProgramTagMutation] = useAddProgramTagMutation();
  const [createProgramTagMutation] = useCreateProgramTagMutation();
  const { data: automationRulesData } = useAutomationRulesQuery({
    variables: { limit: 100 },
    skip: !isOpen,
  });

  useEffect(() => {
    if (!isOpen) {
      setSelectedTags([]);
      setSessionCreatedTags([]);
    }
  }, [isOpen]);

  const handleTagSelected = (tag: ProgramTagDataFragment) => {
    if (!selectedTags.some((t) => t.id === tag.id)) {
      setSelectedTags((prev) => [...prev, tag]);
    }
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagId));
  };

  const handleCreateTag = async (
    name: string,
  ): Promise<ProgramTagDataFragment | null> => {
    try {
      const { data } = await createProgramTagMutation({
        variables: {
          input: { name, color: DEFAULT_TAG_COLOR },
        },
      });
      const created = data?.createProgramTag;
      if (created) {
        const tag: ProgramTagDataFragment = {
          __typename: 'ProgramTag',
          id: created.id,
          name: created.name,
          description: created.description,
          color: created.color,
        };
        setSessionCreatedTags((prev) => [...prev, tag]);
        await refetchMeProviderProgramTags();
        return tag;
      }
    } catch {
      // onError handled by dropdown
    }
    return null;
  };

  const selectedTagIdsUsedAsAutomationTrigger = selectedTags
    .filter((t) =>
      isTagUsedAsAutomationTrigger(
        t.id,
        automationRulesData?.automationRules ?? [],
      ),
    )
    .map((t) => t.id);
  const showAutomationWarning =
    selectedTagIdsUsedAsAutomationTrigger.length > 0;

  const handleSubmit = async () => {
    if (selectedTags.length === 0 || selectedPatients.length === 0) return;

    setIsSubmitting(true);

    const ops: Promise<{ ok: boolean }>[] = [];
    const affectedPatientIds = new Set<string>();
    for (const patient of selectedPatients) {
      const programId = patient.programInstanceId;
      if (!programId) continue;
      const patientTagIds = new Set((patient.tags ?? []).map((t) => t.id));

      for (const tag of selectedTags) {
        if (patientTagIds.has(tag.id)) continue;
        affectedPatientIds.add(programId);
        ops.push(
          addProgramTagMutation({
            variables: {
              input: { programId, programTagId: tag.id },
            },
          })
            .then(() => ({ ok: true }))
            .catch(() => ({ ok: false })),
        );
      }
    }

    if (ops.length === 0) {
      setIsSubmitting(false);
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message="All selected clients already have these tags."
        />
      ));
      return;
    }

    const results = await Promise.all(ops);
    const successCount = results.filter((r) => r.ok).length;
    const failCount = results.length - successCount;

    if (failCount > 0) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="error"
          message={
            successCount === 1
              ? `1 tag application succeeded; ${failCount} could not be added.`
              : `${successCount} tag applications succeeded; ${failCount} could not be added.`
          }
        />
      ));
    } else {
      const affectedCount = affectedPatientIds.size;
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          level="success"
          message={`Tag${
            selectedTags.length > 1 ? 's' : ''
          } added to ${affectedCount} ${
            affectedCount === 1 ? 'client' : 'clients'
          }`}
        />
      ));
    }

    try {
      await onSuccess();
    } finally {
      setIsSubmitting(false);
      setSelectedTags([]);
      setSessionCreatedTags([]);
      onClose();
    }
  };

  const canSubmit =
    selectedTags.length > 0 && selectedPatients.length > 0 && !isSubmitting;

  return (
    <ModalDialog
      title="Add tags to clients"
      isOpen={isOpen}
      setClosed={onClose}
      primaryActionTitle={`Add tag${selectedTags.length > 1 ? 's' : ''} to ${
        selectedPatients.length
      } ${selectedPatients.length === 1 ? 'client' : 'clients'}`}
      primaryActionOnClick={handleSubmit}
      primaryActionDisabled={!canSubmit}
      fetching={isSubmitting}
      width="medium"
      overflow="auto"
      fullHeight
    >
      <div className="space-y-4">
        <div>
          <ProgramTagSelectDropdown
            programTags={[
              ...providerProgramTags,
              ...sessionCreatedTags.filter(
                (t) => !providerProgramTags.some((p) => p.id === t.id),
              ),
            ]}
            value=""
            onChange={() => undefined}
            onTagSelected={handleTagSelected}
            onCreateTag={handleCreateTag}
            id="bulk-add-tag-select"
            label="Select tags to add"
            placeholder="Search or create tag"
            excludeTagIds={selectedTags.map((t) => t.id)}
            onError={(msg) => toast.error(msg)}
          />
          {selectedTags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {selectedTags.map((tag) => {
                const styles = getTagStyles(tag.color ?? DEFAULT_TAG_COLOR);
                return (
                  <span
                    key={tag.id}
                    className={classNames(
                      'text-sm inline-flex items-center gap-1 rounded-full px-2 py-1',
                    )}
                    style={styles}
                  >
                    {tag.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag.id)}
                      className="rounded-full p-0.5 hover:opacity-80"
                      aria-label={`Remove ${tag.name}`}
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {showAutomationWarning && (
          <div className="flex rounded-md bg-orange-25/30 p-4 text-orange-125">
            <InformationCircleIcon
              className="h-5 min-h-[20px] w-5 min-w-[20px] shrink-0 text-orange-100"
              aria-hidden
            />
            <span className="ml-3 font-sans text-caption font-medium">
              One or more selected tags trigger automations. Adding them to
              these clients may run those automations for each client.
            </span>
          </div>
        )}
      </div>
    </ModalDialog>
  );
};

export default BulkAddTagModal;
